import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import { Check, X, FileCode, Code, Images, Info, AlertTriangle, ExternalLink, Bell } from 'lucide-react';

const Disclaimer = () => {
  const tocItems = [
    { id: 'acceptance', label: 'Acceptance of Terms' },
    { id: 'content-usage', label: 'Content Usage' },
    { id: 'intellectual-property', label: 'Intellectual Property' },
    { id: 'disclaimer', label: 'Disclaimers' },
    { id: 'user-conduct', label: 'User Conduct' },
    { id: 'third-party', label: 'Third-Party Links' },
    { id: 'modifications', label: 'Modifications' }
  ];

  return (
    <>
      <Helmet>
        <title>Terms of Service | InvizHer | Programming Tutorials, Projects & Resources</title>
        <meta name="description" content="Read InvizHer's Terms of Service for programming tutorials, projects, and resources. Learn about our content usage policies, intellectual property rights, and user guidelines for accessing developer resources and coding materials." />
        <meta name="keywords" content="InvizHer terms, programming blog terms, coding tutorial terms, developer resources terms, programming tutorials terms of service, code projects terms, development resources policy" />
        <link rel="canonical" href="https://invizher.com/disclaimer" />
        
        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Terms of Service | InvizHer",
            "description": "Read InvizHer's Terms of Service for programming tutorials, projects, and resources. Learn about our content usage policies and guidelines.",
            "publisher": {
              "@type": "Organization",
              "name": "InvizHer",
              "logo": "https://invizher.com/assets/img/inz-logo.png"
            },
            "url": "https://invizher.com/pages/terms",
            "dateModified": "2024-12-29"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-28 pb-16" role="main">
          {/* Hero Section */}
          <section className="container mx-auto px-4 mb-12">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 gradient-text">Terms of Service</h1>
              <p className="text-muted-foreground text-lg mb-8">
                Please read these terms carefully before using our programming blog and resources.
              </p>
              <div className="flex justify-center">
                <span className="text-sm text-muted-foreground">Last updated: December 29, 2024</span>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <div className="container mx-auto px-4">
            <div className="lg:grid lg:grid-cols-4 lg:gap-8">
              {/* Sidebar with Table of Contents */}
              <div className="lg:col-span-1 mb-8 lg:mb-0">
                <div className="glass-morphism rounded-2xl p-6 lg:sticky lg:top-28">
                  <h2 className="text-xl font-bold mb-4 gradient-text">Quick Navigation</h2>
                  <ul className="space-y-3">
                    {tocItems.map((item) => (
                      <li key={item.id}>
                        <a 
                          href={`#${item.id}`} 
                          className="text-muted-foreground hover:text-primary transition-colors text-sm"
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Main Content Sections */}
              <div className="lg:col-span-3">
                <div className="glass-morphism rounded-2xl p-6 lg:p-8 space-y-12">
                  {/* Acceptance of Terms */}
                  <section id="acceptance">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Acceptance of Terms</h2>
                    <div className="space-y-4 text-muted-foreground">
                      <p>
                        By accessing and using InvizHer ("the Website"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. These terms apply to all visitors and users of the Website.
                      </p>
                      <div className="bg-blue-500/10 border-l-4 border-blue-500 p-6 rounded-r-xl">
                        <p className="text-blue-700 dark:text-blue-400">
                          This is a personal blog website focused on sharing programming knowledge and resources. While we strive to provide accurate and helpful information, all content is for educational purposes only.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Content Usage */}
                  <section id="content-usage">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Content Usage</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="glass-morphism p-6 rounded-xl">
                        <h3 className="text-lg font-semibold mb-4">Permitted Use</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground text-sm">Personal learning and reference</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground text-sm">Sharing with proper attribution</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground text-sm">Educational purposes</span>
                          </li>
                        </ul>
                      </div>
                      <div className="glass-morphism p-6 rounded-xl">
                        <h3 className="text-lg font-semibold mb-4">Prohibited Use</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground text-sm">Commercial redistribution</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground text-sm">Unauthorized reproduction</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground text-sm">Content modification</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Intellectual Property */}
                  <section id="intellectual-property">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Intellectual Property</h2>
                    <div className="space-y-4 text-muted-foreground">
                      <p>All content published on InvizHer, including but not limited to:</p>
                      <div className="bg-card/50 rounded-xl p-6">
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <FileCode className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span>Articles, tutorials, and blog posts</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Code className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span>Code samples and snippets</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Images className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span>Images and graphics</span>
                          </li>
                        </ul>
                      </div>
                      <p>is protected by intellectual property laws and remains the property of InvizHer unless otherwise stated.</p>
                    </div>
                  </section>

                  {/* Disclaimers */}
                  <section id="disclaimer">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Disclaimers</h2>
                    <div className="glass-morphism p-6 rounded-xl space-y-4 text-muted-foreground">
                      <p>The content on this Website is provided "as is" and "as available" basis. We make no representations or warranties of any kind, express or implied, regarding:</p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <span>The accuracy or completeness of the content</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <span>The suitability of the content for any purpose</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <span>The reliability of any code samples or tutorials</span>
                        </li>
                      </ul>
                    </div>
                  </section>

                  {/* User Conduct */}
                  <section id="user-conduct">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">User Conduct</h2>
                    <div className="space-y-4 text-muted-foreground">
                      <p>When using our Website, you agree not to:</p>
                      <div className="bg-card/50 rounded-xl p-6">
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                            <span>Attempt to disrupt or compromise the Website's security</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                            <span>Use automated tools to scrape or download content</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                            <span>Misuse or abuse any resources or code samples provided</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                            <span>Engage in any activity that could harm or negatively impact the Website</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Third-Party Links */}
                  <section id="third-party">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Third-Party Links</h2>
                    <div className="glass-morphism p-6 rounded-xl space-y-4 text-muted-foreground">
                      <p>Our Website may contain links to third-party websites or resources. Please note:</p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <ExternalLink className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>We do not endorse or control these third-party sites</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <ExternalLink className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>You access third-party content at your own risk</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <ExternalLink className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <span>We are not responsible for any third-party content or policies</span>
                        </li>
                      </ul>
                    </div>
                  </section>

                  {/* Modifications */}
                  <section id="modifications">
                    <h2 className="text-2xl font-bold mb-6 gradient-text">Modifications</h2>
                    <div className="space-y-4 text-muted-foreground">
                      <p>We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to the Website.</p>
                      <div className="bg-purple-500/10 border-l-4 border-purple-500 p-6 rounded-r-xl">
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <Bell className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                            <span className="text-purple-700 dark:text-purple-400">
                              We will update the "Last Modified" date at the top of this page
                            </span>
                          </li>
                          <li className="flex items-start gap-3">
                            <Bell className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                            <span className="text-purple-700 dark:text-purple-400">
                              Your continued use of the Website constitutes acceptance of any changes
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>
                </div>

                {/* Contact Section */}
                <section className="mt-8 text-center py-16">
                  <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
                  <p className="text-muted-foreground mb-8">
                    Can't find what you're looking for? Feel free to reach out to us directly.
                  </p>
                  <Link 
                    to="/contact" 
                    className="inline-block px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:opacity-90 transition-opacity"
                  >
                    Contact Us
                  </Link>
                </section>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Disclaimer;
